import React from "react";
import styled from "styled-components";
import { useApolloClient } from "@apollo/react-hooks";
import PersonIcon from "@material-ui/icons/Person";
import { sortBy } from "lodash";
import { RouteComponentProps, withRouter } from "react-router";
import { MultiAvatar } from "@sonder/ui/source/styled/components/MultiAvatar";
import { TotalAvatar } from "@sonder/ui/source/styled/fragments/MultiAvatar";
import {
  RelatedTaskBlock as RelatedTaskBlockFragment,
  RelatedTasksStatus,
} from "@sonder/ui/source/styled/fragments/ScheduleLayer";
import { SchedulerHighlightColors } from "@sonder/ui/source/styled/theme";
import { TaskData } from "core/queries/GetTask";
import {
  GetTaskSchedulesPlace,
  GetTaskSchedulesRelatedTask,
  RelatedTaskScheduleFragment,
} from "core/queries/GetTaskSchedules";
import { UserData } from "core/queries/GetUsers";
import { getTaskAssignees } from "core/queries/mappers";
import { titlelizeCase } from "core/utils/titlelizeCase";
import {
  deselectTasks,
  selectTasks,
  setHoveringTask,
  viewItem,
} from "schedule/actions";
import { useStore } from "schedule/store";
import {
  getSpaceCleanInternalRef,
  getStatus,
} from "schedule/utils/scheduleUtils";
import { parseGraphQLId } from "tasks/helpers";

const StyledRelatedTaskBlockFragment = styled(RelatedTaskBlockFragment)`
  min-height: 3.14rem;
  width: 100%;
`;

interface ShuffleIndicatorProps {
  status: RelatedTasksStatus;
}

const StyledShuffleIndicator = styled.div<ShuffleIndicatorProps>`
  background-color: ${props => SchedulerHighlightColors[props.status]};
  border-radius: 0.175rem;
  height: 0.175rem;
  left: 0.175rem;
  position: absolute;
  right: 0.175rem;
  top: 0.175rem;
`;

interface RelatedTaskDecoyProps {
  children?: React.ReactNode;
  task: GetTaskSchedulesRelatedTask;
  place?: GetTaskSchedulesPlace;
  w?: number;
}

interface RelatedTaskBlockProps
  extends RelatedTaskDecoyProps,
    RouteComponentProps<{ taskId: string }> {}

interface RelatedTaskAvatarsProps {
  assignees: User[];
  w: number;
}

const RelatedTaskAvatars = ({ w = 0, assignees }: RelatedTaskAvatarsProps) => {
  const avatarNames = assignees.map(avatar => avatar.name).join(", ");

  if (w <= 1)
    return (
      <TotalAvatar title={avatarNames}>
        <PersonIcon /> {assignees.length}
      </TotalAvatar>
    );

  return <MultiAvatar avatars={assignees} />;
};

export const RelatedTaskBlockSimple = (props: RelatedTaskBlockProps) => {
  const { children, match, task: cachedTask, place, w = 0 } = props;
  const client = useApolloClient();
  const currentRouteTaskId = match.params.taskId || "";
  const taskId = cachedTask?.id || "";
  const task = client.readFragment({
    id: `Task:${taskId}`,
    fragment: RelatedTaskScheduleFragment,
  });
  const {
    cleaningRequest: {
      company: { id: cleaningCompanyId },
    },
  } = task;

  const {
    dispatch,
    state: { isDisabled, isSelecting, modifyingTaskId, selectedTasks },
  } = useStore();

  const assignees = sortBy(
    getTaskAssignees(task as unknown as TaskData).map(assignee => ({
      imageUrl: (assignee as unknown as UserData).avatar?.url,
      ...assignee,
    })),
    ["firstName"],
  );
  const status = getStatus(
    task as GetTaskSchedulesRelatedTask,
  ) as RelatedTasksStatus;
  const isSelected = !!selectedTasks.find(t => t.id === task?.id);
  const isShuffled = task.cleaningRequest.shuffled;
  const isOpen = currentRouteTaskId === parseGraphQLId(taskId);
  return (
    <RelatedTaskBlockFragment
      {...props}
      data-testid="related-task-block"
      disabled={isDisabled}
      isOpen={isOpen}
      isSelected={isSelected}
      isSelecting={isSelecting}
      status={status}
      type="button"
      onClick={e => {
        const taskDetails = {
          id: taskId,
          cleaningCompanyId,
          internalRef:
            (place?.cufSpaceType &&
              titlelizeCase(getSpaceCleanInternalRef(place))) ||
            place?.shortInternalRef ||
            "",
          type: task?.type,
        };
        if (isSelecting) {
          if (isSelected) dispatch(deselectTasks([taskDetails]));
          else dispatch(selectTasks([taskDetails]));

          return;
        }

        // Otherwise, normal flow.
        const boundingRect = e.currentTarget.getBoundingClientRect();
        dispatch(
          viewItem([
            {
              boundingRect,
              place,
              task: task as GetTaskSchedulesRelatedTask,
            },
          ]),
        );
      }}
      onMouseEnter={() => {
        if (!modifyingTaskId) dispatch(setHoveringTask(taskId));
      }}
      onMouseLeave={() => {
        dispatch(setHoveringTask(""));
      }}
    >
      {isShuffled && <StyledShuffleIndicator status={task.status} />}
      <RelatedTaskAvatars assignees={assignees} w={w} />
      {children}
    </RelatedTaskBlockFragment>
  );
};

export const RelatedTaskBlock = withRouter(RelatedTaskBlockSimple);

export const RelatedTaskDecoyBlock = (props: RelatedTaskDecoyProps) => {
  const { task, w = 0 } = props;
  const assignees = getTaskAssignees(task as unknown as TaskData).map(
    assignee => ({
      imageUrl: (assignee as unknown as UserData).avatar?.url,
      ...assignee,
    }),
  );
  const status = getStatus(
    task as GetTaskSchedulesRelatedTask,
  ) as RelatedTasksStatus;
  return (
    <StyledRelatedTaskBlockFragment status={status}>
      <RelatedTaskAvatars assignees={assignees} w={w} />
    </StyledRelatedTaskBlockFragment>
  );
};
